import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faTimes, faSync } from '@fortawesome/free-solid-svg-icons';

import {
  accessLocalStorage,
  listInboundDispatchers,
  updateInboundDispatcherIps,
} from '../../../services';

import '../../../libs/bulma.min.css';
import './TrunksForm.css';

// Página dedicada (master VGS) para gerenciar troncos cuja context='inbound-dispatcher'.
// - Lista os troncos com seus IPs atuais (CSV).
// - Permite editar a lista de IPs inline — um IP por linha — e salvar.
// - Não duplica o fluxo de create (criação fica em TrunksCreate.js → tipo "Inbound Dispatcher").
// - Não altera TrunksList.js nem TrunksEdit.js para evitar regressão em troncos normais.

function TrunksInboundDispatcherManage() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [editState, setEditState] = useState({}); // { provider: { raw, saving, feedback } }

  const bootstrap = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user } = dataUser;
    if (!user || user.role !== 'root' || user.idtenant !== 'vgs') {
      return history.push('/home');
    }
    await reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const reload = async () => {
    setLoading(true);
    const resp = await listInboundDispatchers();
    if (resp.status === 200 && Array.isArray(resp.data)) {
      setRows(resp.data);
      const next = {};
      resp.data.forEach((r) => {
        next[r.provider] = {
          raw: (r.ips_match || '').split(',').filter(Boolean).join('\n'),
          saving: false,
          feedback: null,
        };
      });
      setEditState(next);
    } else {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const updateRaw = (provider, raw) => {
    setEditState((prev) => ({
      ...prev,
      [provider]: { ...(prev[provider] || {}), raw, feedback: null },
    }));
  };

  const saveProvider = async (provider) => {
    const current = editState[provider];
    if (!current) return;
    const ipsMatch = (current.raw || '')
      .split(/[\n,;\s]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
      .join(',');

    setEditState((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], saving: true, feedback: null },
    }));

    const resp = await updateInboundDispatcherIps({ provider, ipsMatch });

    if (resp.status === 200) {
      setEditState((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], saving: false, feedback: { ok: true, msg: 'IPs atualizados com sucesso.' } },
      }));
      await reload();
    } else {
      const msg = (resp.data && resp.data.message)
        || (resp.data && resp.data.invalid && `IPs inválidos: ${resp.data.invalid.join(', ')}`)
        || 'Falha ao atualizar.';
      setEditState((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], saving: false, feedback: { ok: false, msg } },
      }));
    }
  };

  return (
    <div className="trunks-form-container">
      <div className="trunks-form-header">
        <div className="trunks-form-title-wrapper">
          <div>
            <h1 className="trunks-form-title">Troncos Inbound Dispatcher (VGS)</h1>
            <p className="trunks-form-subtitle">
              Gerencie os troncos master da VIGOS que usam o contexto
              <code> inbound-dispatcher </code> e roteiam DIDs via
              <code> incoming_call_configs</code>.
            </p>
          </div>
        </div>
        <div>
          <button className="btn-back" onClick={reload} title="Recarregar">
            <FontAwesomeIcon icon={faSync} />
            Atualizar
          </button>
          {' '}
          <Link to="/config/tronco/lista" className="btn-back">
            <FontAwesomeIcon icon={faArrowLeft} />
            Voltar
          </Link>
          {' '}
          <Link to="/config/tronco/novo" className="btn-submit">
            Novo tronco
          </Link>
        </div>
      </div>

      {loading && <p>Carregando…</p>}

      {!loading && rows.length === 0 && (
        <div className="form-card">
          <p>Nenhum tronco em <code>inbound-dispatcher</code> cadastrado.</p>
          <p>Use <Link to="/config/tronco/novo">Novo tronco</Link> e escolha o tipo <strong>Inbound Dispatcher (VGS)</strong>.</p>
        </div>
      )}

      {!loading && rows.map((r) => {
        const st = editState[r.provider] || { raw: '', saving: false, feedback: null };
        return (
          <div key={r.provider} className="form-card" style={{ marginBottom: 20 }}>
            <div className="inbound-row-header">
              <h2 className="trunks-form-title" style={{ fontSize: '1.1rem' }}>{r.provider}</h2>
              <div>
                <span className="tag">tenant: {r.tenant || '—'}</span>{' '}
                <span className="tag">ipLocal: {r.ip_local || '—'}</span>{' '}
                <span className="tag">codec: {r.codec || '—'}</span>
              </div>
            </div>

            <div className="field">
              <label className="label" htmlFor={`ips-${r.provider}`}>IPs de origem (um por linha)</label>
              <textarea
                id={`ips-${r.provider}`}
                className="textarea"
                rows={Math.max(3, (st.raw || '').split('\n').length + 1)}
                value={st.raw}
                onChange={(e) => updateRaw(r.provider, e.target.value)}
              />
              <p className="form-help-text">
                Aceita IPv4 ou CIDR. A entrada é normalizada no servidor.
              </p>
            </div>

            {st.feedback && (
              <div className={st.feedback.ok ? 'alert-message alert-success' : 'alert-message alert-danger'}>
                <div className="alert-body">
                  {st.feedback.ok ? <strong>OK — </strong> : <strong>Erro — </strong>}
                  {st.feedback.msg}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                className="btn-submit"
                onClick={() => saveProvider(r.provider)}
                disabled={st.saving}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{st.saving ? 'Salvando…' : 'Salvar IPs'}</span>
              </button>
              <button
                className="btn-cancel"
                onClick={() => updateRaw(r.provider, (r.ips_match || '').split(',').filter(Boolean).join('\n'))}
                disabled={st.saving}
              >
                <FontAwesomeIcon icon={faTimes} />
                Desfazer
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TrunksInboundDispatcherManage;
